import React from 'react';
import { Users, Loader2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { formatDate, formatPhone } from '../utils/formatters';

export const WorkdayEmployeeTable = ({
  workdayLoading,
  workdayError,
  workdayEmployees,
  workdayEmployeesToShow,
  workdayTotalItems,
  workdayTotalPages,
  workdayPage,
  setWorkdayPage,
  workdayPageSize,
  setWorkdayPageSize,
  workdayPageSizeOptions,
  workdayRangeStart,
  workdayRangeEnd,
  syncing,
  onSync,
  onEmployeeClick,
  onProcess
}) => {
  return (
    <div className="mb-6">
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg w-full min-w-0">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Users className="w-5 h-5 text-gray-400 shrink-0" />
              <h2 className="text-xl font-semibold text-white">
                Empleados de Workday
              </h2>
              <Badge className="bg-blue-900/50 text-blue-200">
                {workdayTotalItems} {workdayTotalItems === 1 ? 'empleado' : 'empleados'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onSync}
                disabled={syncing || workdayLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-green-600 bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sincronizando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    ACTUALIZAR
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={onProcess}
                disabled={syncing || workdayLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-600 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                PROCESS
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 w-full min-w-0">
          {workdayLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Loader2 className="animate-spin mb-4" size={32} />
              <p>Cargando empleados de Workday...</p>
            </div>
          )}

          {workdayError && !workdayLoading && (
            <div className="rounded-lg border border-red-800 bg-red-900/20 p-4 mb-4">
              <p className="text-red-300">
                <strong>Error:</strong> {workdayError}
              </p>
            </div>
          )}

          {!workdayLoading && !workdayError && (
            <>
              {workdayEmployees.length === 0 ? (
                <div className="text-center py-16">
                  <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">
                    No hay empleados de Workday disponibles.
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-full max-h-[calc(100vh-16rem)] overflow-auto -mx-4 sm:-mx-6">
                    <div className="inline-block min-w-full align-middle w-full">
                      <div className="shadow ring-1 ring-gray-700 rounded-lg w-full">
                        <table className="min-w-full w-full divide-y divide-gray-700">
                          <thead className="sticky top-0 z-10 bg-gray-700/50 shadow-sm">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                Nombre Completo
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                Email
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                Teléfono
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                Fecha Contratación
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                Base Fábrica
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                Tipo Contrato
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                Título
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-gray-800 divide-y divide-gray-700">
                            {workdayEmployeesToShow.map((employee, index) => {
                              const firstName = employee.FirstName || employee.first_name;
                              const lastName = employee.LastName || employee.last_name;
                              const email = employee.Email || employee.email;
                              const phone = employee.Phone || employee.phone;
                              const hireDate = employee.HireDate || employee.hire_date || employee.hireDate;
                              const factory = employee.Factory || employee.factory_base || employee.factoryBase;
                              const businessTitle = employee.BusinessTitle || employee.businessTitle;
                              const wid = employee.WID || employee.wid || employee.id;
                              
                              return (
                                <tr
                                  key={employee.WID || employee.wid || employee.id || index}
                                  onClick={() => wid && onEmployeeClick(wid)}
                                  className={`transition-colors cursor-pointer ${
                                    index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'
                                  } hover:bg-gray-700`}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex flex-col">
                                      <span className="text-sm font-medium text-white">
                                        {firstName && lastName
                                          ? `${firstName} ${lastName}`
                                          : firstName || lastName || employee.name || 'N/A'}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {email ? (
                                      <a
                                        href={`mailto:${email}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-sm text-blue-400 hover:text-blue-300 hover:underline"
                                      >
                                        {email}
                                      </a>
                                    ) : (
                                      <span className="text-sm text-gray-400">N/A</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                    {formatPhone(phone)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                    {formatDate(hireDate)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {factory ? (
                                      <Badge className="bg-violet-900/50 text-violet-200">
                                        {factory}
                                      </Badge>
                                    ) : (
                                      <span className="text-sm text-gray-400">N/A</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                    {employee.contract_type || employee.contractType || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                    <div className="max-w-xs truncate" title={businessTitle || 'N/A'}>
                                      {businessTitle || 'N/A'}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {workdayTotalItems > 0 && (
                    <div className="px-4 sm:px-6 py-4 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-wrap">
                        <p className="text-sm text-gray-500">
                          Mostrando {workdayRangeStart}–{workdayRangeEnd} de {workdayTotalItems} empleados
                        </p>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-400">Mostrar:</label>
                          <select
                            value={workdayPageSize}
                            onChange={(e) => setWorkdayPageSize(Number(e.target.value))}
                            className="px-3 py-1.5 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            {workdayPageSizeOptions?.map((size) => (
                              <option key={size} value={size} className="bg-gray-800">
                                {size}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setWorkdayPage((p) => Math.max(1, p - 1))}
                          disabled={workdayPage <= 1}
                          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Anterior
                        </button>
                        <span className="text-sm text-gray-500 px-2">
                          Página {workdayPage} de {workdayTotalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => setWorkdayPage((p) => Math.min(workdayTotalPages, p + 1))}
                          disabled={workdayPage >= workdayTotalPages}
                          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Siguiente
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
